import { graphql, readInlineData } from 'relay-runtime';
import { getBuyerId } from 'dibs-cookie-jar';
import { getOfferCurrency, getSkuId } from '../helpers/checkoutHelpers';
import { wasPaymentDenied } from '../helpers/checkoutForms';
import {
    getParamFromUrl,
    getCheckoutParam,
    isNegotiableOrder,
    isUserComesFromAcceptedOffer,
    isUserNegotiating,
} from '../helpers/checkoutRedirects';
import { METHODS, TABS } from '../constants/checkoutConstants';
import {
    shipmentQuoteSelectedAction,
    addressSelectedAction,
    offerAmountPerItemChangeAction,
    offerPriceBookNameChangeAction,
    transactionTypeChangeAction,
    setShipmentNotesAction,
    transactionBuyerChangedAction,
    emptyShippingAddressAction,
    setQuantityAction,
    emptyPaymentMethodAction,
    saveCreditCardAction,
    saveBankAccountAction,
    savePaypalAction,
    saveCheckAction,
    saveWireTransferAction,
    saveOnPlatformWireTransferAction,
    saveApplePayAction,
    saveKlarnaPaymentMethodAction,
    completeCreditCardAction,
    completeBankAccountAction,
    submitKlarnaError,
    saveStripePaymentMethodApiType,
    setPaymentProcessor,
    savePaymentMethod,
} from '../actions/commonActions';
import { transactionRecomputedAction } from '../actions/checkoutRelayAction';
import { transactionSetupAction } from '../actions/checkoutUIActions';

const transactionReducerBuyerFragment = graphql`
    fragment checkoutTransactionReducer_buyer on User @inline {
        serviceId
        preferences {
            currency
        }
        firstValidShippingAddress {
            serviceId
        }
        addressBook {
            personalAddress {
                serviceId
            }
            addressList {
                serviceId
                validation {
                    buyerShipping {
                        valid
                    }
                }
            }
        }
        wallet {
            paymentMethods {
                paymentMethod {
                    __typename
                    apiType
                    ... on CreditCard {
                        apiType
                        token
                        isExpired
                        paymentProcessor
                    }
                    ... on ACH {
                        apiType
                        token
                        paymentProcessor
                    }
                    ... on PayPalAccount {
                        token
                        paymentProcessor
                    }
                    ... on ApplePay {
                        token
                        paymentProcessor
                    }
                }
                shippingAddresses {
                    serviceId
                }
            }
        }
    }
`;

const transactionReducerPreCheckoutTransactionFragment = graphql`
    fragment checkoutTransactionReducer_preCheckoutTransaction on Transaction @inline {
        serviceId
        skuId
        isPrivateOffer
        isAutomatedOffer
        currencyConversionRateEvent {
            serviceId
        }
        buyerChargeCurrency
        buyer {
            preferences {
                currency
            }
        }
        shipment {
            shipmentType
            shippingAddress {
                sourceId
            }
        }
        activeQuote: currentOrSubmittedShipmentQuote {
            serviceId
        }
        mostRecentOffer {
            canUseOfferForCheckout
            quantity
            amountPerItem {
                convertedAmountList {
                    amount
                    currency
                }
            }
            priceBookName
        }
        project {
            name
            serviceId
        }
        isAutomatedOffer
    }
`;

function getTransactionType() {
    const isPrivateOffer = isUserComesFromAcceptedOffer();
    const isNegotiate = isUserNegotiating();

    if (isPrivateOffer) {
        return 'PRE_AUTH_NEGOTIATION';
    } else if (isNegotiate) {
        return 'NEGOTIABLE';
    } else {
        return 'NON_NEGOTIABLE';
    }
}

export function getInitialOfferAmountPerItem() {
    return isUserNegotiating() ? Number(getParamFromUrl('offerAmount')) : null;
}

export function getOfferPriceBookName({ transaction }) {
    return getParamFromUrl('priceBookName') || transaction?.mostRecentOffer?.priceBookName || null;
}

const getCurrencyConversionRateEventId = (viewer, item, checkoutOffer) => {
    const existRateId = checkoutOffer?.currencyConversionRateEvent?.serviceId;
    const latestRateId = viewer?.currentRates?.serviceId;
    const isPriceUponRequest = item?.isPriceUponRequest;
    const hasNetPrice = item?.hasNetPrice;
    if (!existRateId && isPriceUponRequest && hasNetPrice) {
        const netPrice = (item?.pricing?.prices || []).find(p => p?.type === 'NET');
        return netPrice?.amount?.conversionRateEvent?.serviceId || latestRateId;
    }
    return (
        existRateId || item?.pricing?.totalAmount?.conversionRateEvent?.serviceId || latestRateId
    );
};

const getDefaultQuantity = (item, preCheckoutTransaction) => {
    const { quantity, hasUnlimitedQuantity } = item || {};
    const preCheckoutQuantity = preCheckoutTransaction?.mostRecentOffer?.quantity;
    const urlQuantity = Number(getCheckoutParam('quantity'));

    return urlQuantity && (hasUnlimitedQuantity || urlQuantity <= quantity)
        ? urlQuantity
        : preCheckoutQuantity || 1;
};

function getDefaultShippingAddressId(buyer, preCheckoutTransaction) {
    const preCheckoutAddressId = preCheckoutTransaction?.shipment?.shippingAddress?.sourceId;
    const personalAddress = buyer?.addressBook?.personalAddress;
    const addressList = buyer?.addressBook?.addressList || [];
    const shippingAddress =
        addressList.find(
            ({ serviceId, validation }) =>
                serviceId === preCheckoutAddressId && !!validation?.buyerShipping?.valid
        ) ||
        personalAddress ||
        buyer?.firstValidShippingAddress;
    return shippingAddress?.serviceId || null;
}

const getDefaultDeliveryOptions = (state, { deliveryOptions = [] }) => {
    const { shipmentQuoteId } = state;
    const shipmentType = state.shipmentType || 'FIRSTDIBS_FACILITATED';
    const is1stdibsFacilitated = shipmentType === 'FIRSTDIBS_FACILITATED';
    const preQuotes = deliveryOptions.filter(q => !q.isLocalPickup);
    const firstQuoteId = preQuotes?.[0]?.serviceId;

    const isCurrentQuoteAvailable =
        shipmentQuoteId &&
        is1stdibsFacilitated &&
        deliveryOptions.some(q => q.serviceId === shipmentQuoteId);

    if (is1stdibsFacilitated && !shipmentQuoteId && preQuotes.length) {
        // no shipment quote selected - select first one
        return { shipmentType, shipmentQuoteId: firstQuoteId };
    } else if (is1stdibsFacilitated && shipmentQuoteId && !isCurrentQuoteAvailable) {
        // shipmentQuote is selected but is no longer valid, select first one or unset
        return { shipmentType, shipmentQuoteId: firstQuoteId };
    } else {
        return { shipmentType };
    }
};

const getDefaultCreditCard = (buyer, shippingAddressId) => {
    return buyer?.wallet?.paymentMethods?.find(({ paymentMethod, shippingAddresses } = {}) => {
        return (
            paymentMethod?.apiType === METHODS.METHOD_CREDIT_CARD &&
            !paymentMethod?.isExpired &&
            shippingAddresses?.some(address => address?.serviceId === shippingAddressId)
        );
    })?.paymentMethod;
};

const getDefaultBankAccountToken = (buyer, shippingAddressId) => {
    const validBankAccounts = (buyer?.wallet?.paymentMethods || []).filter(
        ({ paymentMethod, shippingAddresses = [] }) =>
            paymentMethod.apiType === METHODS.METHOD_ACH &&
            shippingAddresses.find(({ serviceId }) => serviceId === shippingAddressId)
    );
    return validBankAccounts?.[0]?.paymentMethod?.token || null;
};

const getStateAfterAuthAmountChanged = state => ({
    ...state,
    paymentMethodApiType: state.paymentMethodApiType,
});

function getBuyerIdFromEmailToken(emailToken) {
    const index = emailToken.indexOf('_');
    return emailToken.substr(0, index);
}

function getPaymentMethodTypeFromTab(tab) {
    switch (tab) {
        case TABS.TAB_CREDIT_CARD:
            return METHODS.METHOD_CREDIT_CARD;
        case TABS.TAB_PAYPAL_ACCOUNT:
            return METHODS.METHOD_PAYPAL_ACCOUNT;
        case TABS.TAB_WIRE_TRANSFER:
            return METHODS.METHOD_WIRE_TRANSFER;
        case TABS.TAB_CHECK:
            return METHODS.METHOD_CHECK;
        case TABS.TAB_APPLE_PAY:
            return METHODS.METHOD_APPLE_PAY;
    }
    return null;
}

function getPaymentMethodApiType(paymentMethodType) {
    switch (paymentMethodType) {
        case 'CREDIT_CARD':
            return METHODS.METHOD_CREDIT_CARD;
        case 'PAYPAL_ACCOUNT':
            return METHODS.METHOD_PAYPAL_ACCOUNT;
        case 'WIRE_TRANSFER':
            return METHODS.METHOD_WIRE_TRANSFER;
        case 'ON_PLATFORM_WIRE_TRANSFER':
            return METHODS.METHOD_ON_PLATFORM_WIRE_TRANSFER;
        case 'APPLE_PAY':
            return METHODS.METHOD_APPLE_PAY;
        case 'ACH':
            return METHODS.METHOD_ACH;
        case 'KLARNA':
            return METHODS.METHOD_KLARNA;
        default:
            return null;
    }
}

const initialState = {
    transactionType: getTransactionType(),
    isPrivateOffer: null,
    isAutomatedOffer: null,
    transactionId: null,
    itemId: null,
    skuId: getSkuId() || null,
    buyerId: getBuyerId(document.cookie) || null,
    buyerChargeCurrency: null,
    isTaxExempt: false,
    shipmentType: null,
    shipmentQuoteId: null,
    paymentMethodApiType: METHODS.METHOD_CREDIT_CARD,
    paymentMethodType: 'CREDIT_CARD',
    paymentMethodToken: null,
    paymentProcessor: null,
    authorizationAmount: null,
    authorizationCode: null,
    sidemark: null,
    offerNotes: getCheckoutParam('message'),
    shipmentNotes: null,
    offerCurrency: 'USD',
    offerAmountPerItem: getInitialOfferAmountPerItem(),
    offerPriceBookName: null,
    promoCode: getCheckoutParam('promoCode'),
    currencyConversionRateEventId: null,
    shippingAddressId: null,
    quantity: null,
    projectId: null,

    // hold onto the last shipping address id until new delivery options are fetched
    previousShippingAddressId: null,
    isNewUser: false,
    billingAddress: null,
    hasSelectedGift: false,
    giftMessage: null,
    isVatReverseCharge: false,
    userMismatch: false,
};

export function checkoutTransactionReducer(state = initialState, action = {}) {
    switch (action.type) {
        case 'CHECKOUT_BUYER_ITEM_VIEWER_UPDATE': {
            const {
                viewer: viewerRef,
                item: itemRef,
                buyer: buyerRef,
                preCheckoutTransaction: preCheckoutTransactionRef,
            } = action.payload;
            const viewer = readInlineData(
                graphql`
                    fragment checkoutTransactionReducer_viewer on Viewer @inline {
                        chargeCurrencies
                        currentRates {
                            serviceId
                        }
                    }
                `,
                viewerRef
            );

            const item = readInlineData(
                graphql`
                    fragment checkoutTransactionReducer_item on Item @inline {
                        serviceId
                        defaultSkuId
                        hasUnlimitedQuantity
                        quantity
                        isMultiSku
                        vertical
                        pricing {
                            prices {
                                type
                                amount {
                                    convertedAmountList {
                                        amount
                                        currency
                                    }
                                }
                            }
                            totalAmount {
                                conversionRateEvent {
                                    serviceId
                                }
                                convertedAmountList {
                                    amount
                                    currency
                                }
                            }
                        }
                    }
                `,
                itemRef
            );
            const buyer = readInlineData(transactionReducerBuyerFragment, buyerRef);
            const preCheckoutTransaction = readInlineData(
                transactionReducerPreCheckoutTransactionFragment,
                preCheckoutTransactionRef
            );

            // should run at most 2 times
            // 1) first checkout page load, with or without a buyer based on log in status
            // 2) when a logged out user logs in
            const buyerCurrency = buyer?.preferences?.currency;
            const offerQuantity = getDefaultQuantity(item, preCheckoutTransaction);
            const offerCurrency = getOfferCurrency(buyerCurrency);
            const isNegotiate = isUserNegotiating();
            const preCheckoutSkuId = preCheckoutTransaction?.skuId;
            const chargeCurrencies = viewer?.chargeCurrencies;

            let newState = {
                ...state,
                isPrivateOffer: preCheckoutTransaction?.isPrivateOffer,
                isAutomatedOffer: preCheckoutTransaction?.isAutomatedOffer,
                itemId: item.serviceId,
                skuId: getSkuId() || preCheckoutSkuId || item.defaultSkuId || null,
                offerCurrency,
                quantity: offerQuantity,
                buyerChargeCurrency: chargeCurrencies.includes(offerCurrency)
                    ? offerCurrency
                    : 'USD',
                currencyConversionRateEventId: getCurrencyConversionRateEventId(
                    viewer,
                    item,
                    preCheckoutTransaction
                ),
                projectName: preCheckoutTransaction?.project?.name,
                projectId: preCheckoutTransaction?.project?.serviceId,
                offerPriceBookName: getOfferPriceBookName({
                    transaction: preCheckoutTransaction,
                }),
            };

            /**
             * Handle offer amount on pre-rendered checkout make offer flow.
             */
            const offerAmount = getInitialOfferAmountPerItem();
            if (!newState.offerAmountPerItem && offerAmount) {
                newState.offerAmountPerItem = offerAmount;
                newState.transactionType = getTransactionType();
                newState.offerNotes = getCheckoutParam('message');
            }

            //if the buyer has arrived in checkout from the Accept Offer email, ensure the correct buyer is logged in. If not, we show an error and switch to full-price flow
            const emailToken = getParamFromUrl('emailToken') || '';
            const isAcceptOfferFlow = getParamFromUrl('acceptOffer') === 'true';
            const isFromAcceptOfferEmail = emailToken && isAcceptOfferFlow;
            const userMismatch =
                isFromAcceptOfferEmail && buyer?.serviceId !== getBuyerIdFromEmailToken(emailToken);
            if (userMismatch) {
                newState.userMismatch = true;
                newState.transactionType = 'NON_NEGOTIABLE';
            } else if (isFromAcceptOfferEmail) {
                //need to reset the state in case the buyer logs into the correct account in checkout; otherwise, error message will persist
                newState.userMismatch = false;
                newState.transactionType = getTransactionType();
            }

            if (buyer) {
                // buyer charge currency
                const buyerChargeCurrency = chargeCurrencies.includes(buyerCurrency)
                    ? buyerCurrency
                    : 'USD';

                // shipping address
                const shippingAddressId = getDefaultShippingAddressId(
                    buyer,
                    preCheckoutTransaction
                );

                const defaultCreditCard = getDefaultCreditCard(buyer, shippingAddressId);

                newState = {
                    ...newState,
                    buyerChargeCurrency,
                    buyerId: buyer.serviceId,
                    fetchedBuyerId: buyer.serviceId,
                    transactionId: preCheckoutTransaction?.serviceId || null,
                    shippingAddressId,
                    paymentMethodApiType: METHODS.METHOD_CREDIT_CARD,
                    paymentMethodType: 'CREDIT_CARD',
                    paymentMethodToken: defaultCreditCard?.token || null,
                    paymentProcessor: defaultCreditCard?.paymentProcessor || null,
                    defaultBankAccountToken: getDefaultBankAccountToken(buyer, shippingAddressId),
                };

                const canUseOfferForCheckout =
                    preCheckoutTransaction?.mostRecentOffer?.canUseOfferForCheckout;

                const isApoJewelryMsku =
                    preCheckoutTransaction?.isAutomatedOffer &&
                    item?.isMultiSku &&
                    item?.vertical === 'jewelry';

                if (
                    canUseOfferForCheckout &&
                    !isNegotiate &&
                    (newState.skuId === preCheckoutSkuId ||
                        // APOs on msku jewelry items apply to all skus!
                        isApoJewelryMsku)
                ) {
                    let convertedAmountList =
                        preCheckoutTransaction?.mostRecentOffer?.amountPerItem?.convertedAmountList;

                    // for APOs on msku jewelry, pull price from item prices
                    if (isApoJewelryMsku) {
                        const automatedPrice = (item?.pricing?.prices || []).find(
                            p => p?.type === 'AUTOMATED_OFFER'
                        );

                        convertedAmountList = automatedPrice?.amount?.convertedAmountList;
                    }

                    const amountObj = (convertedAmountList || []).find(
                        a => a && a.currency === offerCurrency
                    );

                    newState = {
                        ...newState,
                        transactionType: 'PRE_AUTH_NEGOTIATION',
                        offerAmountPerItem: amountObj?.amount,
                        sidemark: preCheckoutTransaction?.sidemark?.content,
                    };
                }
            }

            return newState;
        }
        case 'CHECKOUT_UPDATE_DELIVERY_OPTIONS': {
            const {
                deliveryOptions: deliveryOptionsRefs,
                preCheckoutTransaction: preCheckoutTransactionRef,
                buyer: buyerRef,
            } = action.payload;
            const deliveryOptions = (deliveryOptionsRefs || []).map(deliveryOptionsRef =>
                readInlineData(
                    graphql`
                        fragment checkoutTransactionReducer_deliveryOptions on ShipmentQuote
                        @inline {
                            serviceId
                            isLocalPickup
                        }
                    `,
                    deliveryOptionsRef
                )
            );
            const preCheckoutTransaction = readInlineData(
                transactionReducerPreCheckoutTransactionFragment,
                preCheckoutTransactionRef
            );
            const buyer = readInlineData(transactionReducerBuyerFragment, buyerRef);

            // Runs every time new delivery options are fetched.
            // This should happen whenever the shipping address is changed.
            const hasTransactionActiveQuote = !!(deliveryOptions || []).find(
                q => q.serviceId === preCheckoutTransaction?.activeQuote?.serviceId
            );
            let newState = {
                ...state,
                previousShippingAddressId: null,
                defaultBankAccountToken:
                    state.paymentMethodApiType === METHODS.METHOD_CREDIT_CARD ||
                    state.paymentMethodApiType === METHODS.METHOD_ACH
                        ? getDefaultBankAccountToken(buyer, state.shippingAddressId)
                        : null,
            };

            if (state.paymentMethodApiType === METHODS.METHOD_ACH) {
                newState.paymentMethodToken = getDefaultBankAccountToken(
                    buyer,
                    state.shippingAddressId
                );
            } else if (state.paymentMethodApiType === METHODS.METHOD_CREDIT_CARD) {
                newState.lastValidCreditCardToken = state.paymentMethodToken;
            }

            // edge case - we selected local pickup before, but then deleted all shipping addresses and added
            // new address, we will use new one shipmentQuoteId to persist local pickup option selection
            if (newState.shipmentType === 'LOCAL_PICKUP' && !newState.shipmentQuoteId) {
                const localPickupOption = (deliveryOptions || []).find(q => q.isLocalPickup);
                newState = {
                    ...newState,
                    shipmentQuoteId: localPickupOption ? localPickupOption?.serviceId : null,
                };
            }

            if (hasTransactionActiveQuote) {
                // default to transaction active quote when it matches the current address (i.e. is found in deliveryOptions)
                newState = {
                    ...newState,
                    shipmentType: preCheckoutTransaction?.shipment?.shipmentType,
                    shipmentQuoteId: preCheckoutTransaction?.activeQuote?.serviceId,
                };
            } else {
                newState = {
                    ...newState,
                    ...getDefaultDeliveryOptions(state, { deliveryOptions }),
                };
            }

            return newState;
        }
        case transactionBuyerChangedAction.toString(): {
            const { buyerId, isNewUser = false } = action.payload;

            return {
                ...state,
                buyerId,
                isNewUser,
            };
        }
        case 'TRANSACTION_BUYER_LOGOUT': {
            return {
                ...state,
                ...initialState,
            };
        }
        case addressSelectedAction.toString(): {
            const { shippingAddress, paymentMethodToken } = action.payload;

            const shippingAddressId = shippingAddress?.serviceId || null;
            const newState = getStateAfterAuthAmountChanged(state);
            return {
                ...newState,
                shippingAddressId,
                paymentMethodToken:
                    newState.paymentMethodApiType === METHODS.METHOD_CREDIT_CARD ||
                    newState.paymentMethodApiType === METHODS.METHOD_ACH
                        ? paymentMethodToken
                        : newState.paymentMethodToken,
                previousShippingAddressId: newState.shippingAddressId,
                isTaxExempt: false,
                isVatReverseCharge: false,
            };
        }
        case offerAmountPerItemChangeAction.toString(): {
            const { newOffer } = action.payload;

            return {
                ...state,
                offerAmountPerItem: newOffer,
            };
        }
        case offerPriceBookNameChangeAction.toString(): {
            const { offerPriceBookName } = action.payload;

            return {
                ...state,
                offerPriceBookName,
            };
        }
        case transactionTypeChangeAction.toString(): {
            const { transactionType } = action.payload;

            return {
                ...state,
                transactionType,
            };
        }
        case emptyShippingAddressAction.toString(): {
            return {
                ...state,
                shippingAddressId: null,
                shipmentQuoteId: null,
                paymentMethodApiType: METHODS.METHOD_CREDIT_CARD,
                paymentMethodType: 'CREDIT_CARD',
                paymentMethodToken: null,
                paymentProcessor: null,
            };
        }
        case shipmentQuoteSelectedAction.toString(): {
            const { shipmentType, shipmentQuoteId } = action.payload;

            return {
                ...state,
                shipmentType,
                shipmentQuoteId,
            };
        }
        case 'TRANSACTION_TAX_EXEMPT': {
            return {
                ...state,
                isTaxExempt: action.isTaxExempt,
            };
        }
        case 'TRANSACTION_VAT_REVERSE_CHARGE': {
            return {
                ...state,
                isVatReverseCharge: action.isVatReverseCharge,
            };
        }
        case saveStripePaymentMethodApiType.toString(): {
            const { paymentMethodApiType } = action.payload;
            return {
                ...state,
                paymentMethodApiType,
            };
        }
        case setPaymentProcessor.toString(): {
            const { paymentProcessor } = action.payload;
            return {
                ...state,
                paymentProcessor,
            };
        }
        case 'CHECKOUT_SELECT_PAYMENT_TAB': {
            const { paymentTab, lastValidCreditCardToken, lastValidBankAccountToken } = action;
            const { paymentMethodApiType: currentMethod, defaultBankAccountToken } = state;
            const selectedWire = paymentTab === TABS.TAB_WIRE_TRANSFER && isNegotiableOrder();
            const selectedBankAccount = paymentTab === TABS.TAB_BANK_ACCOUNT;
            const cameFromWire =
                currentMethod === METHODS.METHOD_WIRE_TRANSFER && isNegotiableOrder();
            const cameFromBankAccount = currentMethod === METHODS.METHOD_ACH;
            let paymentMethodApiType;
            let paymentMethodToken;

            if (selectedWire) {
                paymentMethodApiType = getPaymentMethodTypeFromTab(paymentTab);
                paymentMethodToken = null;
            } else if (cameFromWire || cameFromBankAccount) {
                paymentMethodApiType = METHODS.METHOD_CREDIT_CARD;
                paymentMethodToken = lastValidCreditCardToken;
            } else if (selectedBankAccount) {
                paymentMethodApiType = METHODS.METHOD_ACH;
                paymentMethodToken = lastValidBankAccountToken || defaultBankAccountToken;
            } else {
                paymentMethodApiType = state.paymentMethodApiType;
                paymentMethodToken = state.paymentMethodToken;
            }
            return {
                ...state,
                paymentMethodApiType,
                paymentMethodToken,
            };
        }
        case 'CHECKOUT_SELECT_CREDITCARD': {
            const { token, paymentProcessor } = action;

            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_CREDIT_CARD,
                paymentMethodToken: token,
                paymentProcessor,
            };
        }
        case saveCreditCardAction.toString(): {
            const { token } = action.payload;

            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_CREDIT_CARD,
                paymentMethodToken: token,
            };
        }
        case savePaymentMethod.toString(): {
            const { paymentMethodType, paymentMethodToken, paymentProcessor } = action.payload;
            return {
                ...state,
                paymentMethodType,
                paymentProcessor,
                paymentMethodToken: paymentMethodToken || null,
                paymentMethodApiType: getPaymentMethodApiType(paymentMethodType),
            };
        }
        case 'CHECKOUT_SELECT_BANKACCOUNT': {
            const { token } = action;

            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_ACH,
                paymentMethodToken: token,
            };
        }
        case saveBankAccountAction.toString(): {
            const { token, paymentProcessor } = action.payload;

            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_ACH,
                paymentMethodToken: token,
                paymentProcessor,
            };
        }
        case 'CHECKOUT_CHANGE_BANKACCOUNT_SECTION': {
            const { bankAccountSectionSelected } = action;
            if (bankAccountSectionSelected === 'all') {
                return {
                    ...state,
                    paymentMethodApiType: METHODS.METHOD_ACH,
                    paymentMethodToken: state.lastValidBankAccountToken,
                };
            }
            return state;
        }
        case 'SAVE_APPLE_PAY_TOKEN': {
            const { token } = action;
            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_APPLE_PAY,
                paymentMethodToken: token,
            };
        }
        case savePaypalAction.toString(): {
            const { token } = action.payload;

            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_PAYPAL_ACCOUNT,
                paymentMethodToken: token,
                paymentProcessor: 'BRAINTREE',
            };
        }
        case saveWireTransferAction.toString(): {
            const { billingAddress } = action.payload;
            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_WIRE_TRANSFER,
                paymentMethodToken: null,
                paymentProcessor: 'OFF_PLATFORM',
                billingAddress,
            };
        }
        case saveOnPlatformWireTransferAction.toString(): {
            const { billingAddress } = action.payload;
            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_ON_PLATFORM_WIRE_TRANSFER,
                paymentMethodToken: null,
                paymentProcessor: 'STRIPE',
                billingAddress,
            };
        }
        case saveCheckAction.toString(): {
            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_CHECK,
                paymentMethodToken: null,
            };
        }
        case saveApplePayAction.toString(): {
            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_APPLE_PAY,
                paymentMethodToken: null,
            };
        }
        case saveKlarnaPaymentMethodAction.toString(): {
            const { sessionId, authorizationToken, billingAddress } = action.payload;
            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_KLARNA,
                paymentProcessor: 'KLARNA',
                paymentMethodToken: sessionId,
                authorizationCode: authorizationToken,
                billingAddress,
            };
        }
        case submitKlarnaError.toString(): {
            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_CREDIT_CARD,
                paymentMethodToken: null,
                authorizationCode: null,
            };
        }
        case emptyPaymentMethodAction.toString(): {
            const { method, lastValidCreditCardToken } = action.payload;
            const { paymentMethodApiType } = state;
            const isCreditCard = paymentMethodApiType === METHODS.METHOD_CREDIT_CARD;
            const newState = { ...state, authorizationCode: null };

            if (method === paymentMethodApiType) {
                newState.paymentMethodApiType = METHODS.METHOD_CREDIT_CARD;
                newState.paymentMethodToken = isCreditCard
                    ? null
                    : lastValidCreditCardToken || state.paymentMethodToken;
            }
            return newState;
        }
        case completeCreditCardAction.toString(): {
            const { paymentMethodToken } = action.payload;

            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_CREDIT_CARD,
                paymentMethodToken,
            };
        }
        case completeBankAccountAction.toString(): {
            const { paymentMethodToken, paymentProcessor } = action.payload;

            return {
                ...state,
                paymentMethodApiType: METHODS.METHOD_ACH,
                paymentMethodToken,
                paymentProcessor,
            };
        }
        case 'TRANSACTION_SET_CHARGE_CURRENCY': {
            const { buyerChargeCurrency } = action;
            return {
                ...state,
                buyerChargeCurrency,
            };
        }
        case setQuantityAction.toString(): {
            const { quantity, paymentMethodToken } = action.payload;
            return {
                ...getStateAfterAuthAmountChanged(state),
                quantity,
                paymentMethodToken,
            };
        }
        case 'TRANSACTION_SET_PROMO_CODE': {
            const { promoCode } = action;
            return {
                ...getStateAfterAuthAmountChanged(state),
                promoCode,
            };
        }
        case 'TRANSACTION_REMOVE_PROMO_CODE': {
            return {
                ...getStateAfterAuthAmountChanged(state),
                promoCode: null,
            };
        }
        case 'TRANSACTION_SET_OFFER_NOTES': {
            const { offerNotes } = action;
            return {
                ...state,
                offerNotes,
            };
        }
        case 'CHECKOUT_SET_SIDEMARK': {
            const { sidemark } = action;
            return {
                ...state,
                sidemark,
            };
        }
        case 'CHECKOUT_PROJECT_SET': {
            const { projectId, projectName } = action;
            return {
                ...state,
                projectId,
                projectName,
            };
        }
        case setShipmentNotesAction.toString(): {
            const { shipmentNotes } = action.payload;
            return {
                ...state,
                shipmentNotes,
            };
        }
        case 'TRANSACTION_SUBMIT_ERROR': {
            const { errorStatus, errorMessage } = action;
            if (wasPaymentDenied({ errorStatus, errorMessage })) {
                return {
                    ...state,
                    paymentMethodApiType: METHODS.METHOD_CREDIT_CARD,
                    paymentMethodToken: null,
                };
            }
            return state;
        }
        case 'TRANSACTION_SET_IS_GIFT_SELECTED': {
            const { hasSelectedGift } = action;
            return {
                ...state,
                hasSelectedGift,
            };
        }
        case 'TRANSACTION_SET_GIFT_MESSAGE': {
            const { giftMessage } = action;
            return {
                ...state,
                giftMessage,
            };
        }
        case transactionRecomputedAction.toString(): {
            // todo create inline fragment on transaction
            const { isKlarnaEligible } = action?.payload?.transaction || {};
            const { paymentMethodApiType } = state;

            if (paymentMethodApiType === METHODS.METHOD_KLARNA && !isKlarnaEligible) {
                return {
                    ...state,
                    paymentMethodApiType: METHODS.METHOD_CREDIT_CARD,
                    authorizationCode: null,
                    paymentMethodToken: null,
                };
            }

            return state;
        }
        case transactionSetupAction.toString(): {
            const { transaction: transactionRef } = action.payload;
            const transaction = readInlineData(
                graphql`
                    fragment checkoutTransactionReducer_transaction on Transaction @inline {
                        availablePaymentMethods {
                            paymentMethodType
                            paymentProcessor
                        }
                    }
                `,
                transactionRef
            );

            if (!state.paymentProcessor && state.paymentMethodType === 'CREDIT_CARD') {
                // payment method defaulted to a credit card, but the default payment processor has not been determined yet
                return {
                    ...state,
                    paymentProcessor:
                        transaction?.availablePaymentMethods?.find(
                            method => method?.paymentMethodType === 'CREDIT_CARD'
                        )?.paymentProcessor || null,
                };
            }
            return state;
        }
    }
    return state;
}
